import React, { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { shootCoinsFrom, playCoins, playExplosion, playCelebration, playThud } from "@/lib/confetti";
import CoinStreakBanner from "@/components/CoinStreakBanner";

function animateNumber(set, from, to, ms) {
  const start = performance.now();
  const tick = (now) => {
    const p = Math.min(1, (now - start) / ms);
    set(Math.round(from + (to - from) * (1 - Math.pow(1 - p, 3))));
    if (p < 1) requestAnimationFrame(tick);
  };
  requestAnimationFrame(tick);
}

function Boxer({ gender, punching }) {
  const girl = gender === "girl";
  const glove = "#E53935";
  const top = girl ? "#FF2D95" : "#1E88E5";
  return (
    <svg width="120" height="140" viewBox="0 0 120 140" style={{ overflow: "visible" }}>
      <g>
        <path d="M46 96 q-4 16 -8 30" stroke="#222" strokeWidth="12" fill="none" strokeLinecap="round" />
        <path d="M70 96 q6 16 10 30" stroke="#222" strokeWidth="12" fill="none" strokeLinecap="round" />
        <ellipse cx="36" cy="129" rx="13" ry="6" fill="#FFF" stroke="#111" strokeWidth="2" />
        <ellipse cx="84" cy="129" rx="13" ry="6" fill="#FFF" stroke="#111" strokeWidth="2" />
        <path d="M42 60 q17 -9 34 0 l4 38 q-21 8 -42 0 z" fill={top} stroke="#111" strokeWidth="1.5" />
        {/* rear guard arm */}
        <path d="M46 66 q-10 8 -8 18" stroke={top} strokeWidth="10" fill="none" strokeLinecap="round" />
        <circle cx="40" cy="86" r="10" fill={glove} stroke="#111" strokeWidth="2" />
        {/* lead punching arm */}
        <path d={punching ? "M72 66 q26 -4 44 -8" : "M72 66 q6 10 4 18"} stroke={top} strokeWidth="10" fill="none" strokeLinecap="round" style={{ transition: "d .12s" }} />
        <circle cx={punching ? 118 : 78} cy={punching ? 56 : 86} r="11" fill={glove} stroke="#111" strokeWidth="2" style={{ transition: "cx .12s, cy .12s" }} />
        {/* head */}
        <circle cx="58" cy="36" r="21" fill="#FFD9B3" />
        <circle cx="66" cy="34" r="3" fill="#111" />
        <circle cx="52" cy="34" r="3" fill="#111" />
        <path d="M52 46 q7 5 14 0" stroke="#111" strokeWidth="2.5" fill="none" strokeLinecap="round" />
        {girl && <path d="M38 34 q-5 16 1 26" stroke="#5D3A1A" strokeWidth="7" fill="none" strokeLinecap="round" />}
        <path d="M38 30 a21 21 0 0 1 40 -6 q-20 -8 -38 2z" fill="#111" opacity={girl ? 0 : 0.85} />
        <rect x="40" y="20" width="36" height="6" rx="3" fill={girl ? "#FF2D95" : "#CCFF00"} />
      </g>
    </svg>
  );
}

// Heavy Bag Knockout: boxer steps in, works the bag with quickening combos (coins
// burst on every hit), then a final haymaker EXPLODES the bag in a coin shower + KO!
export default function HeavyBagCelebration({
  coinsAwarded, totalCoins, scorePercent,
  canAdvance, onRetry, onContinue, soundOn = true, hasPrev = false, onPrev, onDone, gender = "boy",
}) {
  const prevTotal = Math.max(0, (totalCoins || 0) - (coinsAwarded || 0));
  const [phase, setPhase] = useState("walkIn"); // walkIn -> combo -> haymaker -> ko -> done
  const [punch, setPunch] = useState(false);
  const [count, setCount] = useState(0);
  const [total, setTotal] = useState(prevTotal);
  const timers = useRef([]);
  const sceneRef = useRef(null);
  const showScore = ["ko", "done"].includes(phase);

  const burst = (xr = 0.62, yr = 0.5) => {
    const r = sceneRef.current?.getBoundingClientRect();
    if (!r) return;
    shootCoinsFrom((r.left + r.width * xr) / window.innerWidth, (r.top + r.height * yr) / window.innerHeight);
  };

  useEffect(() => {
    const add = (fn, ms) => timers.current.push(setTimeout(fn, ms));
    add(() => { setPhase("combo"); if (soundOn) playCelebration(); }, 1700);
    // quickening jab-cross combo — 8 hits, each snaps the glove out and pops coins
    const hits = [0, 620, 1180, 1660, 2060, 2400, 2680, 2920];
    hits.forEach((t, i) => add(() => {
      setPunch(true);
      burst(0.66, 0.42 + (i % 3) * 0.06);
      if (soundOn) { playThud(); if (i % 2) playCoins(4); }
      add(() => setPunch(false), 150);
    }, 1900 + t));
    add(() => { setPhase("haymaker"); setPunch(true); }, 5200);
    add(() => {
      setPhase("ko");
      burst(0.62, 0.4); burst(0.72, 0.55); burst(0.52, 0.5);
      if (soundOn) playExplosion();
      animateNumber(setCount, 0, coinsAwarded, 1400);
      animateNumber(setTotal, prevTotal, totalCoins, 1600);
      for (let i = 0; i < 4; i++) add(() => { burst(0.5 + i * 0.06, 0.45); if (soundOn && i % 2 === 0) playCoins(6); }, 400 + i * 420);
    }, 5650);
    add(() => { setPhase("done"); onDone && onDone(); }, 8600);
    return () => timers.current.forEach(clearTimeout);
    // eslint-disable-next-line
  }, []);

  const bagGone = ["ko", "done"].includes(phase);
  return (
    <div className="text-center py-2 relative scroll-mt-24" data-testid="heavybag-celebration">
      <div ref={sceneRef} className="relative mx-auto w-full max-w-md h-60 overflow-hidden rounded-2xl"
        style={{ background: "linear-gradient(180deg,#1A1024 0%,#2C1B3A 55%,#160D20 100%)" }} data-testid="heavybag-scene">
        <div className="absolute inset-0 pointer-events-none" style={{ background: "radial-gradient(ellipse 65% 45% at 55% 30%, rgba(255,45,149,0.18), transparent 70%)" }} />
        <div className="absolute left-5 top-7 font-display font-extrabold text-2xl select-none" style={{ color: "#CCFF00", opacity: 0.25, transform: "rotate(-7deg)" }}>FIGHT</div>
        <div className="absolute right-5 top-14 font-display font-extrabold text-xl select-none" style={{ color: "#00E5FF", opacity: 0.22, transform: "rotate(5deg)" }}>NIGHT</div>
        <div className="absolute left-0 right-0" style={{ bottom: 0, height: 12, background: "#0B0612" }} />

        {/* heavy bag hanging right of centre */}
        <AnimatePresence>
          {!bagGone && (
            <motion.div key="bag" className="absolute" style={{ left: "62%", top: 0 }} exit={{ opacity: 0, scale: 1.6, transition: { duration: 0.25 } }}>
              <motion.div style={{ transformOrigin: "top center" }}
                animate={phase === "combo" ? { rotate: [0, 7, -4, 6, -3, 0] } : phase === "haymaker" ? { rotate: [0, 18] } : { rotate: [0, 1.5, -1.5, 0] }}
                transition={{ duration: phase === "haymaker" ? 0.3 : 1.1, repeat: phase === "haymaker" ? 0 : Infinity }}>
                <div className="mx-auto" style={{ width: 3, height: 26, background: "#777" }} />
                <div className="rounded-2xl" style={{ width: 52, height: 110, background: "linear-gradient(180deg,#C62828,#8E1B1B)", border: "2px solid #111", boxShadow: "inset -8px 0 14px rgba(0,0,0,0.4)" }}>
                  <div className="font-display font-black text-[10px] text-center pt-9" style={{ color: "#FFD600", transform: "rotate(-90deg)" }}>ELEVATION</div>
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* KO! stamp */}
        <AnimatePresence>
          {bagGone && (
            <motion.div key="ko" className="absolute font-display font-black select-none" style={{ left: "56%", top: 40, color: "#FFD600", textShadow: "3px 3px 0 #B3282D", fontSize: 54 }}
              initial={{ scale: 0, rotate: -18 }} animate={{ scale: [0, 1.5, 1.1], rotate: -12 }} transition={{ duration: 0.5 }} data-testid="heavybag-ko">
              KO!
            </motion.div>
          )}
        </AnimatePresence>

        {/* the boxer */}
        <motion.div className="absolute" style={{ left: "18%", bottom: 6, width: 120, height: 140 }}
          initial={{ x: -320 }} animate={{ x: phase === "haymaker" ? 26 : 0, transition: { duration: phase === "walkIn" ? 1.5 : 0.18, ease: "easeOut" } }}
          data-testid="heavybag-boxer">
          <motion.div animate={phase === "combo" ? { y: [0, -3, 0], transition: { duration: 0.32, repeat: Infinity } } : { y: 0 }}>
            <Boxer gender={gender} punching={punch} />
          </motion.div>
        </motion.div>
      </div>

      <div className={`flex flex-col items-center gap-1.5 mt-2 transition-opacity duration-500 ${showScore ? "opacity-100" : "opacity-0"}`}>
        <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-2xl font-extrabold font-display"
          style={{ background: "linear-gradient(135deg,#FFD600,#FF9E00)", color: "#3d2c00", boxShadow: "0 8px 24px rgba(255,158,0,0.45)" }} data-testid="quiz-xp-awarded">
          <span className="text-2xl">🪙</span>
          <span className="text-xl sm:text-2xl leading-none">+{count.toLocaleString()} coins today</span>
        </div>
        <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-2xl font-extrabold font-display"
          style={{ background: "linear-gradient(135deg,#FFD600,#FF9E00)", color: "#3d2c00", boxShadow: "0 8px 24px rgba(255,158,0,0.45)" }} data-testid="coin-grand-total">
          <span className="text-2xl">🪙</span>
          <span className="text-3xl leading-none text-outline-lime">{total.toLocaleString()}</span>
          <span className="text-xl leading-none">total</span>
        </div>
        {scorePercent != null && <div className="text-3xl font-extrabold font-display text-[#8fb300]" data-testid="quiz-score">{scorePercent}%</div>}
      </div>

      {showScore && <CoinStreakBanner celebrationsEnabled={soundOn} compact className="mt-2 max-w-md mx-auto justify-center text-center" />}

      <AnimatePresence>
        {phase === "done" && (
          <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.35 }} data-testid="coin-done" className="scroll-mb-24">
            <p className="text-black font-semibold max-w-md mx-auto mt-2" data-testid="quiz-encouragement">
              Knockout combo — the next lesson doesn't stand a chance!
            </p>
            <div className="flex items-center justify-center gap-2 mt-3 flex-wrap">
              {hasPrev && <button data-testid="quiz-prev-button" className="btn-secondary text-sm px-4 py-2 whitespace-nowrap" onClick={() => onPrev && onPrev()}>← Prev</button>}
              {onRetry && <button data-testid="quiz-retry-button" className="btn-secondary text-sm px-4 py-2 whitespace-nowrap" onClick={onRetry}>Redo</button>}
              {canAdvance && onContinue && <button data-testid="quiz-continue-button" className="btn-primary text-sm px-4 py-2 whitespace-nowrap" onClick={onContinue}>Next →</button>}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
